import * as queryString from 'query-string';

import { safeRedirectTarget } from './loginRedirect';

// Root-relative only, unlike the OAuth `returnTo` helpers in ./oauthReturnTo, which
// must be absolute. MagicLinkVerificationPage replays this value through
// history.replace, and an absolute URL there is treated as a path — the member would
// land on "/https://…" instead of the campaign page. The API accepts either form,
// so nothing on the backend catches the mistake.
const toRelativeTarget = (url) => {
    const safe = safeRedirectTarget(url);
    return safe?.startsWith('/') ? safe : null;
};

// The link is redeemed from an email, frequently on a different device, so the
// destination has to travel with the token rather than in client state.
export const resolveMagicLinkCallbackUrl = ({ location, inPopUp } = {}) => {
    if (!location) {
        return null;
    }

    // In the popup the member never left the campaign page, so the page itself is the
    // destination. On the standalone /login page the campaign destination is whatever
    // sent them there, which the existing redirect chain carries in `next`.
    if (inPopUp) {
        return toRelativeTarget(`${location.pathname || ''}${location.search || ''}`);
    }

    return toRelativeTarget(queryString.parse(location.search || '').next);
};

// The API emails a link carrying one of these on `auth/magic-link` instead of
// `magic-link`, which the iOS app claims as a universal-link prefix. Both flows below
// only complete in the browser that started them: referral attribution lives in a
// cookie the app cannot read, and the app would sell Power through Apple IAP at full
// price. An unrecognized value is not an error on the API side, it just falls back.
export const MagicLinkContexts = {
    POWER_REFERRAL: 'power_referral',
    POWER_CAMPAIGN: 'power_campaign',
};

const REFERRALS_BASE_PATH = '/referrals';
const POWER_PERKS_PATH = '/power-perks';

// Segment boundary, not a bare prefix: '/referrals-anything' is an unrelated page and
// must not be tagged as a referral just because the strings start the same. Compared
// case-insensitively to match the router rather than the convention: React Router
// compiles these routes with `sensitive: false`, so '/Referrals/member/ABC' really does
// render the referral landing page — and a destination we serve but decline to tag would
// hand the member back the app-interceptable link this whole path exists to avoid.
const isPathAtOrUnder = (path, basePath) => {
    const normalizedPath = path.toLowerCase();
    return normalizedPath === basePath || normalizedPath.startsWith(`${basePath}/`);
};

// safeRedirectTarget still guards this — a `next` an attacker planted should not get to
// pick which path the API emails — but toRelativeTarget deliberately isn't reused:
// its root-relative narrowing exists to protect the history.replace in
// MagicLinkVerificationPage, and the context never navigates anywhere. An absolute URL
// on one of our own hosts is unusable as a callbackUrl yet still classifies fine, so
// both shapes are reduced to their path here.
const toTargetPath = (url) => {
    const safe = safeRedirectTarget(url);
    if (!safe) {
        return null;
    }
    if (safe.startsWith('/')) {
        return safe.split('?')[0].split('#')[0];
    }
    return new URL(safe).pathname;
};

// Derived from the same two sources as resolveMagicLinkCallbackUrl — the popup sits on
// the destination itself, the standalone /login page carries it in `next` — and the two
// have to keep agreeing about where the member is headed.
export const resolveMagicLinkContext = ({ location, inPopUp } = {}) => {
    if (!location) {
        return null;
    }

    const target = inPopUp
        ? `${location.pathname || ''}${location.search || ''}`
        : queryString.parse(location.search || '').next;
    const path = toTargetPath(target);
    if (!path) {
        return null;
    }

    if (isPathAtOrUnder(path, REFERRALS_BASE_PATH)) {
        return MagicLinkContexts.POWER_REFERRAL;
    }
    if (isPathAtOrUnder(path, POWER_PERKS_PATH)) {
        return MagicLinkContexts.POWER_CAMPAIGN;
    }
    return null;
};
